import React, { useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  Box, Typography, Paper, TextField, Button, CircularProgress, Alert,
  Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Link,
} from '@mui/material';
import supabase from '../../../utils/supabase';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ReCAPTCHA from "react-google-recaptcha";
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

export default function AdminTeamContact() {
  const { t } = useTranslation('adminTeam');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // null, 'submitting', 'success', 'error'
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [v2ChallengeOpen, setV2ChallengeOpen] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();

  const validate = () => {
    const newErrors = {};
    if (!name) newErrors.name = t('contactForm.nameRequired', 'Name is required.');
    if (!subject) newErrors.subject = t('contactForm.subjectRequired', 'Subject is required.');
    if (!message) newErrors.message = t('contactForm.messageRequired', 'Message is required.');
    if (!email) {
      newErrors.email = t('contactForm.emailInvalid', 'Please enter a valid email address.');
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = t('contactForm.emailInvalid', 'Please enter a valid email address.');
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const submitForm = async (v2Token = null) => {
    setSubmitting(true); // Disable form fields
    setSubmitStatus('submitting');
    setStatusDialogOpen(true);

    try {
      const requestBody = { name, email, subject, message };

      if (v2Token) {
        requestBody.recaptchaToken = v2Token;
        requestBody.verificationType = 'v2';
      } else {
        if (!executeRecaptcha) throw new Error("Recaptcha v3 not available");
        requestBody.recaptchaToken = await executeRecaptcha('contactForm');
        requestBody.verificationType = 'v3';
      }

      const { error } = await supabase.functions.invoke('contact-form', {
        body: requestBody,
      });

      if (error) {
        // Check for the specific error from our backend to trigger v2
        if (error.context?.v2_required) {
          setStatusDialogOpen(false);
          setV2ChallengeOpen(true);
          return; // Stop execution here, wait for v2 verification
        }
        throw error;
      }
      
      setSubmitStatus('success');
      setName('');
      setEmail('');
      setSubject('');
      setMessage('');
    } catch (error) {
      console.error('Error submitting form:', error);
      // Only set error status if it's not a v2 challenge request
      if (!v2ChallengeOpen) {
        setSubmitStatus('error');
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      submitForm();
    }
  };

  const handleV2Verification = (token) => {
    setV2ChallengeOpen(false);
    submitForm(token); // Resubmit with the v2 token
  }

  const handleStatusDialogClose = () => {
    setStatusDialogOpen(false);
    setSubmitting(false); // Re-enable form
    setTimeout(() => {
      setSubmitStatus(null);
    }, 300);
  };

  const titleSx = {
    fontWeight: 800,
    letterSpacing: "0.01em",
    color: "text.primary",
    textAlign: "center",
    mb: 1,
  };

  const subtitleSx = {
    textAlign: "center",
    whiteSpace: "pre-line",
    color: "text.secondary",
    maxWidth: 820,
    mx: "auto",
    mb: 4,
  };

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h2" sx={titleSx}>
        {t('contactTitle', 'Contact')}
      </Typography>
      <Typography variant="body1" sx={subtitleSx}>
        {t('contactDescription', 'Contact information for the admin team.')}
      </Typography>
      <Paper sx={{ p: 3, maxWidth: 'md', mx: 'auto' }}>
        <Typography variant="body1" gutterBottom>
          {t('contactPageContent', 'For inquiries regarding this database, please use the contact form below.')}
        </Typography>        

        <Box component="form" onSubmit={handleSubmit} noValidate>
          <Typography variant="h5" component="h3" gutterBottom sx={{ mt: 2 }}>
            {t('contactForm.title', 'Contact Form')}
          </Typography>
          <TextField
            fullWidth
            margin="normal"
            id="name"
            label={t('contactForm.nameLabel', 'Name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            required
            disabled={submitting}
          />
          <TextField
            fullWidth
            margin="normal"
            id="email"
            label={t('contactForm.emailLabel', 'Email Address')}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            error={!!errors.email}
            helperText={errors.email}
            required
            disabled={submitting}
          />
          <TextField
            fullWidth
            margin="normal"
            id="subject"
            label={t('contactForm.subjectLabel', 'Subject')}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            error={!!errors.subject}
            helperText={errors.subject}
            required
            disabled={submitting}
          />
          <TextField
            fullWidth
            margin="normal"
            id="message"
            label={t('contactForm.messageLabel', 'Message')}
            multiline
            rows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            error={!!errors.message}
            helperText={errors.message}
            required
            disabled={submitting}
          />
          <Typography variant="caption" component="div" display="block" sx={{ mt: 1, color: 'text.secondary' }}>
            <Trans
              i18nKey="contactForm.largeFileShareNote"
              ns="adminTeam"
              components={[
                <Link href="https://wetransfer.com/" target="_blank" rel="noopener noreferrer" underline="always" />,
                <Link href="https://gigafile.nu/" target="_blank" rel="noopener noreferrer" underline="always" />
              ]}
            />
          </Typography>
          <Box sx={{ mt: 2, position: 'relative' }}>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
              fullWidth
            >
              {t('contactForm.submitButton', 'Submit')}
            </Button>
          </Box>
        </Box>
      </Paper>
      <Dialog open={statusDialogOpen} onClose={handleStatusDialogClose}>
        <DialogTitle>
          {submitStatus === 'submitting' && t('contactForm.submittingTitle')}
          {submitStatus === 'success' && t('contactForm.submitSuccessTitle')}
          {submitStatus === 'error' && t('contactForm.submitErrorTitle')}
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', minWidth: '300px' }}>
          {submitStatus === 'submitting' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, p: 2 }}>
              <CircularProgress />
              <Typography>{t('contactForm.submittingContent')}</Typography>
            </Box>
          )}
          {submitStatus === 'success' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, p: 2 }}>
              <CheckCircleOutlineIcon color="success" sx={{ fontSize: 60 }} />
              <Typography>{t('contactForm.submitSuccessContent')}</Typography>
            </Box>
          )}
          {submitStatus === 'error' && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, p: 2 }}>
              <ErrorOutlineIcon color="error" sx={{ fontSize: 60 }} />
              <Typography>{t('contactForm.submitErrorContent')}</Typography>
            </Box>
          )}
        </DialogContent>
        {submitStatus !== 'submitting' && (
            <DialogActions>
                <Button onClick={handleStatusDialogClose}>{t('contactForm.closeButton')}</Button>
            </DialogActions>
        )}
      </Dialog>
      <Dialog open={v2ChallengeOpen} onClose={() => setV2ChallengeOpen(false)}>
        <DialogTitle>{t('contactForm.v2Title')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <ReCAPTCHA
              sitekey={process.env.REACT_APP_RECAPTCHA_V2_SITE_KEY}
              onChange={handleV2Verification}
            />
        </DialogContent>
        <DialogActions>
            <Button onClick={() => setV2ChallengeOpen(false)}>
                {t('contactForm.cancelButton')}
            </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
